<template>
  <div class="tv-shell">
    <turnover-desktop-bridge v-if="isDesktopListLayout" />
    <turnover-mobile v-else />
    <print-studio
      v-model="printStudio"
      report="accounting-turnover"
      :title="$t('turnover') + (turnoverAccountName ? ' · ' + turnoverAccountName : '')"
      :subtitle="printSubtitle"
      :columns="printColumns"
      :source="printSource"
      @error="serverErrorHandeler"
    />
    <accounting-doc-view
      v-model="docViewOpen"
      :item="docViewItem"
      @edit="docViewOpen = false; edit({ head_id: $event.id })"
      @copied="copied"
      @error="serverErrorHandeler"
    />
    <!--    filter dialog-->
    <v-dialog
      v-model="filter"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="960"
      scrollable
      content-class="acf-wrap"
    >
      <div class="acf" :style="proListRootStyle" dir="rtl">
        <header class="acf-head">
          <span class="acf-head__icon"><v-icon size="20">mdi-filter-variant</v-icon></span>
          <div class="acf-head__titles">
            <div class="acf-head__title">{{ $t("turnoverFilterTitle") }}</div>
            <div class="acf-head__sub">
              <template v-if="activeFilterChips.length">
                <span class="acf-badge">{{ faNum(activeFilterChips.length) }}</span>
                {{ $t("accFilterActive") }}
              </template>
              <template v-else>{{ $t("accFilterNone") }}</template>
            </div>
          </div>
          <button type="button" class="acf-x" :title="$t('close')" @click="filter = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </header>

        <div class="acf-body">
          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-file-tree-outline</v-icon>
              {{ $t("turnoverFilterAccounts") }}
            </div>
            <div class="acf-grid acf-grid--3">
              <autocomplete v-model="group" title="group" type="group" />
              <autocomplete v-model="total" title="total" type="total" />
              <autocomplete v-model="given" title="given" type="given" />
              <autocomplete v-model="fin4" title="detail" type="detail" />
              <autocomplete v-model="fin5" title="fin5" type="detail" />
              <autocomplete v-model="fin6" title="fin6" type="detail" />
            </div>
          </section>

          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-calendar-range</v-icon>
              {{ $t("turnoverFilterRange") }}
            </div>
            <div class="acf-grid acf-grid--4">
              <div>
                <v-text-field
                  id="tvFromDate"
                  v-model="fromDate"
                  :label="$t('from') + ' ' + $t('date')"
                  prepend-inner-icon="mdi-calendar-start"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="fromDate"
                  element="tvFromDate"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  auto-submit
                  :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  @close="setDate"
                />
              </div>
              <div>
                <v-text-field
                  id="tvToDate"
                  v-model="toDate"
                  :label="$t('until') + ' ' + $t('date')"
                  prepend-inner-icon="mdi-calendar-end"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="toDate"
                  element="tvToDate"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  :min="fromDate"
                  auto-submit
                  :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  @close="setDate"
                />
              </div>
              <v-text-field
                v-model="fromNum"
                :label="$t('from-number')"
                outlined
                dense
                clearable
                hide-details
              />
              <v-text-field
                v-model="toNum"
                :label="$t('toNumber')"
                outlined
                dense
                clearable
                hide-details
              />
            </div>
          </section>

          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-shape-outline</v-icon>
              {{ $t("turnoverFilterOther") }}
            </div>
            <div class="acf-grid acf-grid--4">
              <autocomplete
                v-model="currencyFilter"
                title="currency"
                type="currency"
                :filter="true"
                :spCurrency="true"
              />
              <autocomplete v-model="branch" title="branch" type="branch" :filter="true" :multiple="true" />
              <status v-model="sellStatus" :hideconfig="true" />
              <tag v-model="tag" />
            </div>
          </section>

          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-eye-settings-outline</v-icon>
              {{ $t("turnoverFilterDisplay") }}
            </div>
            <div class="acf-switches">
              <label v-for="o in displayToggles" :key="o.key" class="acf-switch">
                <input
                  type="checkbox"
                  :checked="isOn(o)"
                  @change="setOn(o, $event.target.checked)"
                /><i />
                <span>{{ o.label }}</span>
              </label>
            </div>
          </section>
        </div>

        <footer class="acf-foot">
          <button type="button" class="acf-btn acf-btn--primary" @click="setFilter">
            <v-icon size="16">mdi-filter-check-outline</v-icon>
            {{ $t("accFilterApply") }}
          </button>
          <button
            type="button"
            class="acf-btn acf-btn--ghost"
            :disabled="!activeFilterChips.length"
            @click="resetFilters"
          >
            <v-icon size="16">mdi-filter-remove-outline</v-icon>
            {{ $t("accDocListClearFilters") }}
          </button>
          <span class="acf-spacer" />
          <button type="button" class="acf-btn" @click="filter = false">{{ $t("cancel") }}</button>
        </footer>
      </div>
    </v-dialog>
    <v-dialog
      v-model="taskDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="90%"
    >
      <v-card>
        <v-col class="d-flex">
          <v-btn icon color="grey darken-2" @click="taskDialog = false">
            <v-icon class="close-hover" large> mdi-close </v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("task") }}
          </p>
        </v-col>
        <v-row class="ma-0">
          <task
            task-type="account"
            :ref-id="refId"
            :task-id="taskId"
            @close="taskDialog = false"
          />
        </v-row>
        <v-card-actions />
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="deletDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="400"
    >
      <v-card>
        <v-col>
          <v-btn icon class="close-hover" @click="deletDialog = false">
            <v-icon color="grey" large> mdi-close </v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <v-img class="mx-auto" width="200" src="@/assets/stop.svg" />
        </v-col>
        <v-col class="mt-3 pr-7">
          <p class="mx-auto font-weight-medium">
            {{ $t("are-you-sure-delete-this-case") }}
          </p>
        </v-col>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn v-if="selected.length > 0" color="primary" @click="removegroup">
                {{ $t("remove") }}
              </v-btn>
              <v-btn v-if="selected.length == 0" color="primary" @click="remove">
                {{ $t("remove") }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="deletDialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <dialog-component :dialog-prop="innerComponent" @close="innerComponent = false" />
    <snakbar
      v-model="snakbar"
      :text="snakbarText"
      :active="snakbar"
      :color="snakbarColor"
    />
  </div>
</template>

<script>
import Task from "../task/task";
import General from "../../../../Mixins/general";
import autocomplete from "../../components/general/patial/autocomplete";
import {
  applyColumnSettings,
  loadColumnSettings,
} from "@/plugins/columnSettings/columnSettings";
import { reorderPinnedHeaders } from "@/plugins/columnPin/columnPin";
import ProList from "@/Mixins/proList";
import PrintStudio from "@/modules/print/PrintStudio.vue";
import TurnoverDesktopBridge from "./layouts/desktop/TurnoverDesktopBridge.vue";
import TurnoverMobile from "./layouts/mobile/TurnoverMobile.vue";
import AccountingDocView from "./layouts/AccountingDocView.vue";
export default {
  name: "Turnover",
  provide() {
    return { turnoverList: this };
  },
  components: {
    TurnoverDesktopBridge,
    TurnoverMobile,
    PrintStudio,
    AccountingDocView,
    Snakbar: () => import("../../components/general/snakbar/snakbar"),
    Tag: () => import("../../components/general/Tag/tag"),
    Status: () => import("../../components/general/Status/status"),
    dialogComponent: () => import("../../components/general/dialog/index"),
    autocomplete,
    Task,
  },
  mixins: [General, ProList],
  props: [
    "input",
    "refId",
    "accountProp",
    "idProp",
    "selfProp",
    "mobileShow",
    // 'groupProp',
    // 'totalProp',
    // 'givenProp',
    // 'detailProp',
    // 'fin5Prop',
    // 'fin6Prop',
    // 'fromMonth',
    // 'toMonth',
    // 'fromDateProp',
    // 'toDateProp',
    // 'branchProp',
    // 'fromNumberProp',
    // 'toNumberProp',
    // 'tagProp'
  ],
  data: () => ({
    expanded: [],
    excelItems: [],
    selected: [],
    pageCount: 1,
    page: 1,
    printDialog: false,
    deletDialog: false,
    showSelect: true,
    sumAmount: 0,
    showSummaryTransferContent: null,
    sumAmountPreviousPage: 0,
    filter: false,
    showInvoiceDetail: null,
    productStatus: null,
    fromDate: null,
    toDate: null,
    productStatusItems: null,
    currencyFilter: null,
    model: null,
    from: 1,
    taskDialog: null,
    tag: null,
    temp: null,
    deleteItem: null,
    sellStatus: null,
    items: [],
    sortBy: [],
    account: null,
    dialog: null,
    timer: 0,
    totalItems: 0,
    snakbar: false,
    typeForm: null,
    taskId: null,
    snakbarText: null,
    loading: true,
    options: {
      itemsPerPage: 5,
    },
    // Separate from General mixin money `options` (beforeMount overwrites it).
    tableOptions: {
      page: 1,
      itemsPerPage: 10,
    },
    branch: null,
    printStudio: false,
    docViewOpen: false,
    docViewItem: null,
    showAdditionalDescription: null,
    headers: null,
    printHeaders: null,
    type: null,
    search: null,
    selectedHeaders: [],
    headersMap: null,
    fin4: null,
    fin5: null,
    fin6: null,
    prePage: 1,
    pageData: [],
    given: null,
    group: null,
    total: null,
    showRemain: false,
    fromNum: null,
    toNum: null,
    currentFinancialYear: true,
    showGivenColumn: false,
  }),
  computed: {
    isDesktopListLayout() {
      if (this.mobileShow) return true;
      return this.$vuetify.breakpoint.width >= 800;
    },
    proListKey() {
      return this.turnoverColumnPersistKey;
    },
    proListLocked() {
      return this.turnoverLockedColumns;
    },
    // Columns shown in the table: column menu (hiddenColumns) + account rule.
    tableHeaders() {
      var hidden = this.hiddenColumns;
      return this.showHeaders.filter(function (h) {
        return hidden.indexOf(h.value) === -1;
      });
    },
    turnoverAccountName() {
      if (this.account) return this.account;
      if (this.accountProp && this.accountProp.name) return this.accountProp.name;
      return "";
    },
    closingRemain() {
      var last = this.items && this.items.length ? this.items[this.items.length - 1] : null;
      return last ? this.remainOf(last) : 0;
    },
    displayToggles() {
      return [
        { key: "showAdditionalDescription", label: this.$t("showAdditionalDescription"), on: "active" },
        { key: "showInvoiceDetail", label: this.$t("showInvoiceDetail"), on: "active" },
        { key: "showSummaryTransferContent", label: this.$t("showSummaryTransferContent"), on: "active" },
        { key: "showRemain", label: this.$t("Remaining show from before"), on: true },
        { key: "currentFinancialYear", label: this.$t("currentFinancialYear"), on: true },
      ];
    },
    activeFilterChips() {
      var app = this;
      var name = function (v) {
        if (Array.isArray(v)) return v.length > 1 ? String(v.length) : name(v[0]);
        if (v && typeof v === "object") return v.name || v.text || "";
        return v == null ? "" : String(v);
      };
      var has = function (v) {
        if (Array.isArray(v)) return v.length > 0;
        return v !== null && v !== undefined && v !== "";
      };
      return [
        ["group", app.$t("group")],
        ["total", app.$t("total")],
        ["given", app.$t("given")],
        ["fin4", app.$t("detail")],
        ["fin5", app.$t("fin5")],
        ["fin6", app.$t("fin6")],
        ["fromDate", app.$t("from") + " " + app.$t("date")],
        ["toDate", app.$t("until") + " " + app.$t("date")],
        ["fromNum", app.$t("from-number")],
        ["toNum", app.$t("toNumber")],
        ["currencyFilter", app.$t("currency")],
        ["branch", app.$t("branch")],
        ["sellStatus", app.$t("status")],
        ["tag", app.$t("tag")],
      ]
        .filter(function (d) { return has(app[d[0]]); })
        .map(function (d) { return { key: d[0], label: d[1], value: name(app[d[0]]) }; });
    },
    printColumns() {
      var app = this;
      var named = function (k) {
        return function (r) { return r[k] ? r[k].name : ""; };
      };
      return [
        { key: "number", label: app.$t("number"), format: "number", width: 0.8, value: function (r) { return r.head ? r.head.number : ""; } },
        { key: "date", label: app.$t("date"), width: 1, value: function (r) { return r.date ? r.date : r.head && r.head.date; } },
        { key: "content", label: app.$t("content"), width: 3, value: function (r) { return app.contentText(r); } },
        { key: "given", label: app.$t("given"), width: 1.4, value: named("given") },
        { key: "fin4", label: app.$t("fin4"), width: 1.3, value: named("fin4") },
        { key: "fin5", label: app.$t("fin5"), width: 1.1, value: named("fin5"), defaultVisible: false },
        { key: "fin6", label: app.$t("fin6"), width: 1.1, value: named("fin6"), defaultVisible: false },
        { key: "debtor", label: app.$t("debtor"), format: "money", sum: true, width: 1.3, value: function (r) { return app.amountOf(r, "debtor"); } },
        { key: "creditor", label: app.$t("creditor"), format: "money", sum: true, width: 1.3, value: function (r) { return app.amountOf(r, "creditor"); } },
        { key: "remain", label: app.$t("remain"), format: "money", width: 1.4, value: function (r) { return app.remainOf(r); } },
        { key: "status", label: app.$t("status"), width: 0.5, value: function (r) { return app.statusLabel(r); } },
        { key: "currency", label: app.$t("currency"), width: 0.6, defaultVisible: false, value: function (r) { return r.currency ? r.currency.symbol : ""; } },
      ];
    },
    printSource() {
      var app = this;
      return {
        currentRows: function () { return app.items || []; },
        total: function () { return app.totalItems || 0; },
        fetchPage: function (page, perPage) {
          var options = Object.assign({}, app.tableOptions, { page: page, itemsPerPage: perPage });
          return app.$axios
            .get(app.$endpoint + "accountingreport", { params: app.listParams(page, options) })
            .then(function (res) {
              var d = res.data;
              return { rows: d.data || [], lastPage: d.last_page || 1, total: d.total || 0 };
            });
        },
      };
    },
    printSubtitle() {
      return this.activeFilterChips
        .map(function (c) { return c.label + (c.value ? ": " + c.value : ""); })
        .join(" · ");
    },
    turnoverColumnPersistKey() {
      // Fixed key: this component is also embedded in dialogs/widgets,
      // so the layout must not depend on the host route name.
      return "accounting-turnover-list-columns";
    },
    turnoverLockedColumns() {
      // Fixed sticky block: select + row + document number (no unpin/drag)
      var locked = ["row", "head.number"];
      return this.showSelect ? ["__select"].concat(locked) : locked;
    },
    turnoverColumnPlugins() {
      // show-select inserts a leading checkbox <th>; pad visibleHeaders so
      // pin/resize/drag index mapping stays aligned with thead cells.
      var selectHeader = {
        value: "__select",
        text: "",
        width: 32,
        sortable: false,
        pinned: false,
        originalIndex: -1,
      };
      var visible = this.showSelect
        ? [selectHeader].concat(this.tableHeaders || [])
        : this.tableHeaders || [];
      return {
        headers: this.headers,
        visibleHeaders: visible,
        locked: this.turnoverLockedColumns,
        anchor: "head.number",
        minWidth: 60,
        persistKey: this.turnoverColumnPersistKey,
      };
    },
    // showHeaders () {
    //   var app = this
    //   return this.headers.filter(function (s) {
    //     if (app.accountProp) {
    //       if (s.value === 'fin4.name' || s.value === 'given.name') {
    //       } else {
    //         return app.selectedHeaders.includes(s)
    //       }
    //     } else {
    //       return app.selectedHeaders.includes(s)
    //     }
    //   })
    // },
    showHeaders() {
      const app = this;
      return app.headers.filter((h) => {
        if (app.accountProp) {
          if (h.value === "fin4.name" || h.value === "given.name") {
            return false;
          }
          return app.selectedHeaders.includes(h);
        }
        return app.selectedHeaders.includes(h);
      });
    },
    totals: function () {
      const totals = this.items.reduce(
        (acc, d, index) => {
          acc.debtor += d.debtor
            ? this.currencyFilter && this.currencyFilter !== this.$store.state.currency.id
              ? parseFloat(d.debtor_currency)
              : parseFloat(d.debtor)
            : 0;
          acc.creditor += d.creditor
            ? this.currencyFilter && this.currencyFilter !== this.$store.state.currency.id
              ? parseFloat(d.creditor_currency)
              : parseFloat(d.creditor)
            : 0;
          return acc;
        },
        {
          debtor: 0,
          creditor: 0,
        }
      );
      return totals;
    },
  },
  watch: {
    //checking path for full update
    "$route.fullPath"() {
      this.printDialog = false;
      this.getItems();
    },
    //bufix query
    "$route.query.filters": {
      handler(newVal, oldVal) {
        this.fetchFiltersFromQuery();
      },
      immediate: true,
    },
    showRemain(val) {
      // console.log(val)
    },
    currencyFilter(val) {
      // console.log(JSON.stringify(val))
    },
    accountProp(val) {
      this.getItems();
    },
    idProp() {
      this.sumAmount = 0;
      this.getItems();
    },
    refId(val, oldVal) {
      // this.items = []
      this.sumAmount = 0;
      // this.getItems()
    },
    model() {
      this.$emit("input", this.model);
    },
    selected() {
      this.$emit("input", this.selected.text);
      if (this.selected.length > 0) {
        this.model = this.selected[0].text;
        this.temp = this.selected[0].text;
      }
      this.dialog = false;
    },
    search() {
      var app = this;
      app.loading = true;
      app.page = 1;
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        app.sumAmount = 0;
        app.pageData = [];
        app.getItems();
      }, 1000);
    },
    tableOptions: {
      handler() {
        var app = this;
        app.sortBy = [];
        (app.tableOptions.sortBy || []).map(function (value, key) {
          const index = app.headers.findIndex((h) => h.value === value);
          app.sortBy.push(app.headers[index].table);
        });
        this.getItems();
      },
      deep: true,
    },
    sortBy() {
      const index = this.headers.findIndex((h) => h.value === this.sortBy);
      if (index > -1) {
        this.sortBy = this.headers[index].table;
      }
    },
  },
  activated() {
    // this.sumAmount = 0
    // this.pageData = []
    this.getItems();
  },
  beforeMount() {
    const storedValue = localStorage.getItem("showGivenColumn");
    // If a value exists in localStorage, parse it as a boolean.
    // Otherwise, default to true (or your desired default).
    if (storedValue !== null) {
      this.showGivenColumn = storedValue === "true";
    } else {
      this.showGivenColumn = true; // Or false, depending on your default
    }

    if (this.$route.name === "purchaseList") {
      this.type = "purchase";
    }
    if (this.$route.name === "presaleList") {
      this.type = "presale";
    }
    if (this.$route.name === "sellList") {
      this.type = "sell";
    }
    if (this.$route.name === "purchaseReturnList") {
      this.type = "purchaseReturn";
    }
    if (this.$route.name === "sellReturnList") {
      this.type = "sellReturn";
    }
    this.headers = [
      {
        text: this.$t("row"),
        align: "start",
        sortable: false,
        value: "row",
        width: 50,
      },
      {
        text: this.$t("number"),
        sortable: true,
        value: "head.number",
        table: "doc_header.number",
        width: 70,
      },
      {
        text: this.$t("content"),
        sortable: false,
        value: "contentTurnover",
        width: 250,
      },
      {
        text: this.$t("date"),
        sortable: true,
        value: "head.date",
        table: "doc_header.date",
        width: 100,
      },
      {
        text: this.$t("given"),
        sortable: true,
        value: "given",
        table: "financial_given_coding.name",
        width: 150,
      },
      {
        text: this.$t("fin4"),
        sortable: true,
        value: "fin4",
        table: "financial_detail_coding.name",
        width: 90,
      },
      {
        text: this.$t("fin5"),
        sortable: true,
        value: "fin5",
        table: "financial_detail_coding.name",
        width: 90,
      },
      {
        text: this.$t("fin6"),
        sortable: true,
        value: "fin6",
        table: "financial_detail_coding.name",
        width: 90,
      },
      {
        text: this.$t("debtor"),
        sortable: true,
        value: "debtor",
        table: "doc_article.debtor",
        width: 120,
      },
      {
        text: this.$t("creditor"),
        sortable: true,
        value: "creditor",
        table: "doc_article.creditor",
        width: 120,
      },
      {
        text: this.$t("remain"),
        sortable: false,
        value: "sumAmount",
        width: 150,
      },
      {
        text: this.$t("status"),
        value: "remainStatus",
        width: 70,
      },
      {
        text: this.$t("currency"),
        sortable: false,
        value: "currency",
        width: 80,
      },
      {
        text: this.$t("tag"),
        sortable: false,
        value: "head.tags",
        width: 100,
      },
      {
        text: this.$t("registerar"),
        sortable: false,
        value: "user.name",
        width: 100,
      },
    ];
    this.printHeaders = [
      {
        text: this.$t("number"),
        value: "head",
        width: 5,
      },
      {
        text: this.$t("content"),
        value: "contentTurnover",
        width: 20,
      },
      {
        text: this.$t("date"),
        value: "head.date",
        width: 9,
      },
      {
        text: this.$t("given"),
        value: "given",
        width: 12,
      },
      {
        text: this.$t("fin4"),
        value: "fin4",
        width: 11,
      },
      {
        text: this.$t("debtor"),
        value: this.resolveDebtor(),
        width: 10,
        type: "number",
        sum: true,
      },
      {
        text: this.$t("creditor"),
        value: this.resolveCreditor(),
        width: 10,
        type: "number",
        sum: true,
      },
      {
        text: this.$t("remain"),
        value: "remain",
        width: 15,
        type: "number",
      },
      {
        text: "",
        value: "remainStatus",
        width: 5,
      },
    ];

    reorderPinnedHeaders(this.headers, {
      locked: this.turnoverLockedColumns,
      anchor: "head.number",
    });
    this.loadTurnoverColumnSettings();
    this.headersMap = this.headers;
    this.selectedHeaders = this.headers;
    if (this.fin5Prop) {
      this.fin5 = this.fin5Prop;
    }
    if (this.fin6Prop) {
      this.fin6 = this.fin6Prop;
    }
    this.productStatusItems = [
      {
        value: "active",
        text: this.$t("active"),
      },
      {
        value: "deactive",
        text: this.$t("deactive"),
      },
    ];
    if (this.input) {
      this.model = this.input;
    }
  },
  mounted() {},
  created() {
    this.fetchFiltersFromQuery();
  },
  methods: {
    loadTurnoverColumnSettings() {
      var app = this;
      if (!app.headers || !app.headers.length) return;
      loadColumnSettings(app, app.turnoverColumnPersistKey).then(function (snapshot) {
        if (!snapshot || !snapshot.length) return;
        applyColumnSettings(app.headers, snapshot, {
          locked: app.turnoverLockedColumns,
          anchor: "head.number",
        });
        app.$forceUpdate();
      });
    },
    isFilteredCurrency() {
      return (
        this.currencyFilter && this.currencyFilter.id !== this.$store.state.currency.id
      );
    },
    updatePrintCurrencyHeaders() {
      this.printHeaders.forEach((header) => {
        if (header.text === this.$t("debtor") && header.sum) {
          header.value = this.resolveDebtor();
        }
        if (header.text === this.$t("creditor") && header.sum) {
          header.value = this.resolveCreditor();
        }
      });
    },
    changeShowGiven() {
      localStorage.setItem("showGivenColumn", this.showGivenColumn);
    },
    openPrintDialog() {
      // Kept for callers of the old print dialog; printing now uses Print Studio.
      this.openPrintStudio();
    },
    fetchFiltersFromQuery() {
      if (this.$route.query.filters) {
        const turnoverFilters = JSON.parse(this.$route.query.filters);
        this.group = turnoverFilters.groupProp;
        this.total = turnoverFilters.totalProp;
        this.given = turnoverFilters.givenProp;
        this.fin4 = turnoverFilters.detailProp;
        this.fin5 = turnoverFilters.fin5Prop;
        this.fin6 = turnoverFilters.fin6Prop;
        // set fromDate and toDate with query
        this.fromDate =
          turnoverFilters.fromDateProp != null
            ? turnoverFilters.fromDateProp
            : turnoverFilters.fromMonth != null
            ? turnoverFilters.fromMonth
            : null;
        this.toDate =
          turnoverFilters.toDateProp != null
            ? turnoverFilters.toDateProp
            : turnoverFilters.toMonth != null
            ? turnoverFilters.toMonth
            : null;
        this.branch = turnoverFilters.branchProp;
        this.fromNum = turnoverFilters.fromNumberProp;
        this.toNum = turnoverFilters.toNumberProp;
        this.tag = turnoverFilters.tagProp;
      }
    },
    resolveDebtor() {
      // user filter currency
      if (this.currencyFilter && this.currencyFilter.name) {
        // filter currency != base currency
        if (this.currencyFilter.name != this.$store.state.currency.name) {
          return "debtor_currency";
        } else {
          return "debtor";
        }
      } else {
        return "debtor";
      }
    },
    resolveCreditor() {
      // user filter currency
      if (this.currencyFilter && this.currencyFilter.name) {
        // filter currency != base currency
        if (this.currencyFilter.name != this.$store.state.currency.name) {
          return "creditor_currency";
        } else {
          return "creditor";
        }
      } else {
        return "creditor";
      }
    },
    amountOf(item, key) {
      var cur = this.currencyFilter && this.currencyFilter !== this.$store.state.currency.id;
      var v = cur ? item[key + "_currency"] : item[key];
      return parseFloat(v) || 0;
    },
    remainOf(item) {
      var d = item.currency && item.currency.decimal;
      var v = Number(item.cumulative_debtor) - Number(item.cumulative_creditor) + Number(item.initial);
      return isNaN(v) ? 0 : Number(v.toFixed(d || 0));
    },
    // Negative remains are shown in parentheses (accounting style).
    signed(v, decimal) {
      var n = Number(v) || 0;
      var txt = this.$numberFormat(Math.abs(n), decimal);
      return n < 0 ? "(" + txt + ")" : txt;
    },
    contentText(item) {
      if (item.head && item.head.ref_type === "transfer" && this.showSummaryTransferContent) {
        return this.$t("transferDocumentFrom") + " " + (item.fin4 ? item.fin4.name : "");
      }
      return item.content || "";
    },
    docLink(item) {
      return item ? this.$baseURL + "/accounting/edit/" + item.head_id : "";
    },
    copied() {
      this.snakbarText = this.$t("accDocListLinkCopied");
      this.snakbarColor = "green";
      this.snakbar = true;
    },
    rowClass(item) {
      return item.reference && item.reference.article && this.showInvoiceDetail ? "tv-row--detail" : "";
    },
    openDocView(item) {
      var h = item.head || {};
      this.docViewItem = {
        id: item.head_id,
        number: h.number,
        lock: h.lock,
        doc_type: h.doc_type,
        last_status: h.last_status,
      };
      this.docViewOpen = true;
    },
    openPrintStudio() {
      this.printStudio = true;
    },
    isOn(o) {
      return o.on === true ? !!this[o.key] : this[o.key] === o.on;
    },
    setOn(o, val) {
      this[o.key] = val ? o.on : o.on === true ? false : null;
    },
    resetFilters() {
      var app = this;
      ["group", "total", "given", "fin4", "fin5", "fin6", "fromDate", "toDate", "fromNum", "toNum", "currencyFilter", "branch", "sellStatus", "tag"].forEach(function (k) {
        app[k] = null;
      });
    },
    clearFilterField(key) {
      this[key] = null;
      this.setFilter();
    },
    clearAllFilters() {
      this.resetFilters();
      this.search = null;
      this.setFilter();
    },
    statusLabel(item) {
      const total = +item.cumulative_debtor - +item.cumulative_creditor + +item.initial;
      if (total > 0) {
        return this.$t("deb");
      } else if (total < 0) {
        return this.$t("cred");
      } else {
        return "";
      }
    },
    openPrint(item) {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "accounting", {
          params: {
            print: "active",
            id: [item.head_id],
            type: "document2",
          },
        })
        .then((res) => {
          res = res.data;
          app.$router.push({
            name: "reportViewer",
            params: { dataProp: res, reportProp: "document2" },
          });
          app.printItems = [];
          // var routeData = app.$router.resolve({ name: 'reportViewer', params: { dataProp: res } })
          // window.open(routeData.href, '_blank')
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    openComponent(component, id) {
      this.dialogData = {
        name: component,
        id: id,
      };
      this.innerComponent = true;
    },
    setFilter() {
      this.sumAmount = 0;
      this.pageData = [];
      this.getItems();
    },
    openDeleteDialog(item) {
      this.deleteItem = item;
      this.deletDialog = true;
    },
    openTaskDialog(item) {
      this.refId = item.rel_id;
      this.taskId = item.id;
      this.taskDialog = true;
    },
    isEnabled(slot) {
      return this.enabled === slot;
    },
    edit(item) {
      this.$router.push({ name: "accountingEdit", params: { id: item.head_id } });
    },
    // Query params shared by the table and the print module.
    listParams(page, options) {
      var app = this;
      return {
        fin4: app.fin4 ? app.fin4.id : null,
        fin5: app.fin5 ? app.fin5.id : null,
        fin6: app.fin6 ? app.fin6.id : null,
        given: app.given ? app.given.id : null,
        group: app.group ? app.group.id : null,
        total: app.total ? app.total.id : null,
        idProp: app.idProp,
        account:
          app.$route.name === "turnoverDetail" &&
          app.$route.params.id &&
          !app.$route.params.type
            ? { fin_id: app.$route.params.id }
            : app.accountProp,
        fromDate: app.fromDate,
        showInvoiceDetail: app.showInvoiceDetail,
        branch: app.branch,
        toDate: app.toDate,
        search: app.search,
        page: page,
        refId: app.refId,
        type: app.type,
        report: "turnover",
        sortBy: app.sortBy,
        self: app.selfProp,
        status: app.productStatus,
        options: options,
        currency: app.currencyFilter ? app.currencyFilter.id : null,
        tag: app.tag,
        typeForm: app.typeForm,
        showRemain: app.showRemain,
        fromNumber: app.fromNum,
        toNumber: app.toNum,
        current_financial_year: app.currentFinancialYear,
      };
    },
    getItems() {
      if (this.$store.state.teacherAccess && this.selfProp === undefined) {
        return false;
      }
      var app = this;
      app.filter = false;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "accountingreport", {
          params: app.listParams(
            app.allRows ? app.excelPage : app.tableOptions.page || 1,
            app.tableOptions
          ),
        })
        .then((res) => {
          res = res.data;
          if (app.tableOptions.page === 1) {
            app.sumAmount = 0;
            app.pageData = [];
          }
          if (!app.allRows) {
            app.items = res.data;
            app.totalItems = res.total;
            app.pageCount = res.last_page;
            app.from = res.from;
            app.creatExcelData(res.data);
          } else {
            app.generateExcellForAllData(res.data, res.last_page);
          }
          if (app.tableOptions.page > 1) {
            const index = app.pageData.findIndex(
              (h) => h.page === parseInt(app.tableOptions.page)
            );
            if (index > -1) {
              const i = app.pageData.findIndex(
                (h) => h.page === parseInt(app.tableOptions.page) - parseFloat(1)
              );
              app.sumAmount = app.pageData[i].sum;
            }
          }
          app.items.map(function (value, key) {
            if (
              app.currencyFilter &&
              app.currencyFilter !== app.$store.state.currency.id
            ) {
              app.sumAmount =
                parseFloat(app.sumAmount) +
                (value.debtor_currency ? parseFloat(value.debtor_currency) : 0) -
                (value.creditor_currency ? parseFloat(value.creditor_currency) : 0);
            } else {
              app.sumAmount =
                parseFloat(app.sumAmount) +
                (value.debtor ? parseFloat(value.debtor) : 0) -
                (value.creditor ? parseFloat(value.creditor) : 0);
            }
            value.sum = app.sumAmount;
          });
          if (app.tableOptions.page > 1) {
            const index = app.pageData.findIndex(
              (h) => h.page === parseInt(app.tableOptions.page)
            );
            if (index > -1) {
              // const i = app.pageData.findIndex(h => h.page === parseInt(app.tableOptions.page) - parseFloat(1))
              // app.sumAmount = app.pageData[i].sum
            } else {
              app.pageData.push({ page: app.tableOptions.page, sum: app.sumAmount });
            }
          } else {
            app.pageData.push({ page: app.tableOptions.page, sum: app.sumAmount });
          }
          if (app.$route.params.id) {
            app.account = app.items[0].fin4.name;
          } else {
            app.account = null;
          }
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    remove() {
      var app = this;
      if (!app.deleteItem) return false;
      app.loading = true;
      app.$axios
        .delete(this.$endpoint + "invoice/" + this.deleteItem.id)
        .then((res) => {
          res = res.data;
          app.deletDialog = false;
          app.deleteItem = null;
          app.getItems();
        })
        .catch((err) => {
          this.deletDialog = false;
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    removegroup() {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "invoice", {
          params: {
            deleteItems: app.selected,
          },
        })
        .then((res) => {
          res = res.data;
          app.deletDialog = false;
          app.deleteItem = null;
          app.selected = [];
          app.getItems();
        })
        .catch((err) => {
          this.deletDialog = false;
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    creatExcelData(data, type = "page") {
      var app = this;
      if (type === "page") {
        app.excelItems = [];
      }
      data.map(function (d) {
        app.excelItems.push({
          date: d.head ? d.head.date : "",
          number: d.head ? d.head.number : "",
          gdate: d.head ? d.head.g_date : "",
          content: d.content ? d.content : "",
          given: d.given ? d.given.name : "",
          fin4: d.fin4 ? d.fin4.name : "",
          fin5: d.fin5 ? d.fin5.name : "",
          fin6: d.fin6 ? d.fin6.name : "",
          debtor: d.debtor ? d.debtor : "",
          creditor: d.creditor ? d.creditor : "",
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
div.v-data-table__wrapper {
  min-height: 50px !important;
}
::v-deep .v-data-table table thead tr th,
::v-deep .v-data-table table tbody tr td {
  white-space: nowrap;
}
::v-deep
  .v-text-field--outlined.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  min-height: 32px !important;
}

/* Only when showInvoiceDetail nests the articles table inside a row:
   lift dense 40px clip so product names are fully visible. Other rows unchanged. */
::v-deep
  .app-list-table--dense
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:has(.turnover-invoice-detail) {
  height: auto !important;
  max-height: none !important;
}
::v-deep
  .app-list-table--dense
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:has(.turnover-invoice-detail)
  > td {
  height: auto !important;
  max-height: none !important;
  min-height: 40px !important;
  overflow: visible !important;
  vertical-align: top !important;
}
::v-deep
  .app-list-table--dense
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:has(.turnover-invoice-detail) {
  white-space: normal !important;
  max-width: none !important;
}
.turnover-invoice-detail {
  min-width: 280px;
}
::v-deep .turnover-invoice-detail__table {
  min-width: 280px;
}
::v-deep .turnover-invoice-detail__table table {
  table-layout: fixed;
  width: 100%;
}
::v-deep .turnover-invoice-detail__col-name {
  width: 38%;
}
::v-deep .turnover-invoice-detail__col-count {
  width: 12%;
}
::v-deep .turnover-invoice-detail__col-fee,
::v-deep .turnover-invoice-detail__col-sum {
  width: 25%;
}
::v-deep .turnover-invoice-detail__table table thead tr th,
::v-deep .turnover-invoice-detail__table table tbody tr td {
  white-space: normal !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
::v-deep .turnover-invoice-detail__count {
  white-space: nowrap !important;
}

/* Desktop view uses the shared pro-list kit (namespaced under .pl / .acf) */
@import "~@/sass/design-system/list/pro-list";

.pl .tv-rowno {
  min-width: 18px;
  text-align: center;
}
.pl .tv-content {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.pl .tv-content__text {
  max-width: 100%;
  flex: 0 1 auto;
}
.pl .tv-ref {
  flex: 0 0 auto;
  cursor: pointer;
}
.pl .tv-sub {
  font-size: 11px;
  color: var(--pl-primary);
  white-space: normal;
}
.pl .tv-neg {
  color: #c62828;
}
/* Rows that show the invoice detail grow to fit it */
.pl .pl-table ::v-deep tr.tv-row--detail td {
  height: auto !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  vertical-align: top;
  white-space: normal;
}
.pl .tv-invoice {
  margin-top: 6px;
  border: 1px solid var(--pl-border);
  border-radius: var(--pl-radius);
  overflow: hidden;
  min-width: 300px;
}
.pl .tv-invoice table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11.5px;
}
.pl .tv-invoice th {
  padding: 4px 8px;
  background: var(--pl-surface-2);
  color: var(--pl-muted);
  font-weight: 600;
  text-align: start;
}
.pl .tv-invoice td {
  padding: 3px 8px;
  border-top: 1px solid var(--pl-border);
}
.acf-switches {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 16px;
}
.acf-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  cursor: pointer;
  user-select: none;
}
.acf-switch input {
  display: none;
}
.acf-switch i {
  position: relative;
  flex: 0 0 auto;
  width: 30px;
  height: 17px;
  border-radius: 17px;
  background: #cbd5e1;
  transition: background 0.15s;
}
.acf-switch i::after {
  content: "";
  position: absolute;
  top: 2px;
  right: 2px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #fff;
  transition: right 0.15s;
}
.acf-switch input:checked + i {
  background: var(--pl-primary);
}
.acf-switch input:checked + i::after {
  right: 15px;
}
@media (max-width: 799px) {
  .acf-switches {
    grid-template-columns: 1fr;
  }
}
</style>
